import Swal from 'sweetalert2';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Component } from '@angular/core';
import moment from 'moment';
import { BooleanInput } from 'ng-zorro-antd/core/types';
import { IPreviewDraft } from 'src/app/interfaces/Detailed/IPreviewDraftDetailed';
import { IMedicationDetailed } from 'src/app/interfaces/Medication/IMedicationDetailed';
import { IScheduledDays } from 'src/app/interfaces/Medication/IScheduledDays';
import { date_format, datetime_format } from 'src/date/date.configuration';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { AllergiesExpandComponent } from '../components/alerts-section/allergies/allergies-expand/allergies-expand.component';
import { ProcedureExpandComponent } from '../components/medical-problems-section/procedure/procedure-expand/procedure-expand.component';
import { MedicalDeviceExpandComponent } from '../components/medical-problems-section/medical-device/medical-device-expand/medical-device-expand.component';
import { CurrentProblemsExpandComponent } from '../components/medical-problems-section/current-problems/current-problems-expand/current-problems-expand.component';
import { Router } from '@angular/router';
import { NzSelectOptionInterface } from 'ng-zorro-antd/select';
import {
  IDynamicItem,
  IDynamicTable,
} from 'src/app/interfaces/DynamicTable/IDynamicDetailed';
import { IQuestionnaireDetailed } from 'src/app/interfaces/Questionnaire/IQuestionnaireDetailed';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { TranslateService } from '@ngx-translate/core';
import { COLORS } from 'src/styles/colors';
import { SwalToastService } from 'src/app/services/swal.service';
import {
  IQuestionnaire,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import { EQuestionType } from 'src/app/constants/constants';
import { GetAnswerDto } from 'src/app/interfaces/Questionnaire/IQuestionnaireAnswers';
import { CarePlanService } from 'src/app/services/carePlan.service';

const SCHEDULED_DAY_LABELS: Array<{
  key: keyof IScheduledDays;
  label: string;
}> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

@Component({
  selector: 'app-preview-summary',
  templateUrl: './preview-summary.component.html',
  styleUrls: ['./preview-summary.component.css'],
  standalone: false,
})
export class PreviewSummaryComponent {
  public allergiesExpandComp = AllergiesExpandComponent;
  public procedureExpandComp = ProcedureExpandComponent;
  public medicalDeviceExpandComp = MedicalDeviceExpandComponent;
  public problemExpandComp = CurrentProblemsExpandComponent;

  private destroy$ = new Subject<void>();

  data?: IPreviewDraft;

  switchValue?: boolean = false;

  pdValue?: number;

  pdList: NzSelectOptionInterface[] = [];

  noteValue?: string = '';

  isLoading: boolean = false;
  isPdLoading: BooleanInput = false;

  questionnaireData: any[] = [];

  questionnaireSections: Array<{
    questionnaireId: number;
    questionnaireTemplateId: number;
    title: string;
    rows: any[];
  }> = [];

  constructor(
    private readonly eocService: EpisodeOfCareService,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly carePlanService: CarePlanService,
  ) {}

  handleEndedValue(value: boolean) {
    this.eocService.eocEndedSub.next(value);
  }

  setPD(id: number) {
    this.eocService.setPD(id);
  }

  setNote(note: any) {
    this.eocService.setNote(note);
  }

  isInpatientView() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  ngOnInit() {
    this.isLoading = true;
    this.eocService.unvalidatePreview();
    this.eocService.unsetNote();
    this.eocService.unsetPd();
    this.eocService.eocEndedSub.next(false);

    this.eocService.potentialDiagnosisSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: number | undefined) => {
        this.pdValue = data || undefined;
      });

    this.isPdLoading = true;
    this.loadPreviewDraft();

    this.carePlanService.carePlanChangedSub
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadPreviewDraft();
      });

    this.eocService.getPotentialDiagnosis().subscribe({
      next: (d) => {
        this.pdList = d?.data?.map((d) => {
          return {
            label: d.name,
            value: d.id,
          };
        });
        this.isPdLoading = false;
      },
      error: (err) => {
        this.swalToastService.toastError(
          'Unable to retrieve Potential Diagnosis',
        );
      },
    });
  }

  private loadPreviewDraft() {
    const visitId = this.eocService.currentVisitId();

    if (!visitId) {
      return;
    }

    this.isLoading = true;
    this.eocService.getPreviewDraft(visitId)?.subscribe({
      next: async (d) => {
        this.data = d?.data;

        if (this.data?.getAllCurrentMedicationsDto?.getAll) {
          this.data.getAllCurrentMedicationsDto.getAll =
            this.data.getAllCurrentMedicationsDto.getAll.map((item) =>
              this.enrichMedicationForTable(item),
            );
        }

        if (this.data?.getAllPastMedicationsDto) {
          this.data.getAllPastMedicationsDto =
            this.data.getAllPastMedicationsDto.map((item) =>
              this.enrichMedicationForTable(item),
            );
        }

        if (
          this.data?.getAllCarePlans?.length ||
          this.data?.getAllActiveCarePlans?.length ||
          this.data?.getAllPastCarePlans?.length
        ) {
          try {
            const dropdownResponse = await firstValueFrom(
              this.carePlanService.getDropdownData(),
            );
            this.enrichPreviewCarePlans(dropdownResponse.data);
          } catch {
            // Keep preview usable when terminology metadata is unavailable.
          }
        }

        if (d?.data?.getPotentialDiagnosis?.id) {
          this.eocService.setPD(d?.data?.getPotentialDiagnosis?.id);
        }

        if (d?.data?.isEmpty == false) {
          this.eocService.validatePreview();
        }

        await this.loadAllQuestionnaires();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.swalToastService.toastError('Unable to retrieve Draft Records');
      },
    });
  }

  // Builds the display-only fields the medication table needs (period range,
  // scheduled days summary, product name truncated via CSS when it overflows
  // its column) since the backend only sends the raw data these are derived
  // from.
  private enrichMedicationForTable(
    item: IMedicationDetailed,
  ): IMedicationDetailed {
    const result: IMedicationDetailed & { [key: string]: any } = {
      ...item,
      periodCombination: this.getMedicationPeriodLabel(
        item?.onSetDateTime,
        item?.endDateTime,
      ),
      scheduledDaysCombination: this.getScheduledDaysLabel(item?.scheduledDays),
    };

    if (item?.productCombination) {
      result['__badge__productCombination'] = {
        text: item.productCombination,
        className: 'truncate-cell',
        title: item.productCombination,
      };
    }

    return result;
  }

  private formatMedicationDate(value?: string): string | null {
    if (!value) {
      return null;
    }

    const parsed = moment(
      value,
      [moment.ISO_8601, datetime_format, date_format],
      true,
    );
    return parsed.isValid() ? parsed.format(date_format) : value;
  }

  private getMedicationPeriodLabel(
    onSetDateTime?: string,
    endDateTime?: string,
  ): string {
    const start = this.formatMedicationDate(onSetDateTime);

    if (!start) {
      return 'N/A';
    }

    const end = this.formatMedicationDate(endDateTime) ?? 'Present';

    return `From ${start} to ${end}`;
  }

  private getScheduledDaysLabel(scheduledDays?: IScheduledDays | null): string {
    const selectedDays = scheduledDays
      ? SCHEDULED_DAY_LABELS.filter((day) => scheduledDays[day.key])
      : [];

    if (selectedDays.length === SCHEDULED_DAY_LABELS.length) {
      return 'Everyday';
    }

    if (selectedDays.length === 0) {
      return 'N/A';
    }

    return selectedDays.map((day) => day.label).join(', ');
  }

  private enrichPreviewCarePlans(dropdownData: any) {
    if (!this.data) {
      return;
    }

    if (this.data.getAllCarePlans?.length) {
      this.data.getAllCarePlans =
        this.carePlanService.enrichTerminologyDisplays(
          this.data.getAllCarePlans,
          dropdownData,
        );
    }

    if (this.data.getAllActiveCarePlans?.length) {
      this.data.getAllActiveCarePlans =
        this.carePlanService.enrichTerminologyDisplays(
          this.data.getAllActiveCarePlans,
          dropdownData,
        );
    }

    if (this.data.getAllPastCarePlans?.length) {
      this.data.getAllPastCarePlans =
        this.carePlanService.enrichTerminologyDisplays(
          this.data.getAllPastCarePlans,
          dropdownData,
        );
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLaboratoryData(
    laboratoryData: IDynamicTable<IDynamicItem>[],
    groupId: number,
  ) {
    return laboratoryData.filter((dataItem) => dataItem.groupId == groupId);
  }

  getCurrentCareTeamsForPreview() {
    return (this.data?.getAllCareTeams ?? [])
      .filter((careTeam) => careTeam.isActive)
      .map((careTeam) => ({
        ...careTeam,
        onSetDate: careTeam.onSetDateTime,
        endDate: careTeam.endDateTime,
      }));
  }

  getPastCareTeamsForPreview() {
    return (this.data?.getAllCareTeams ?? [])
      .filter((careTeam) => !careTeam.isActive)
      .map((careTeam) => ({
        ...careTeam,
        onSetDate: careTeam.onSetDateTime,
        endDate: careTeam.endDateTime,
      }));
  }

  private async loadAllQuestionnaires(): Promise<void> {
    const questionnaires = this.data?.getAllQuestionnaires ?? [];
    if (!questionnaires.length) {
      this.questionnaireSections = [];
      return;
    }

    // Keep spinner on while we load questionnaire details
    this.isLoading = true;

    // Keep latest questionnaire per templateId using MAX(id)
    const latestByTemplate = new Map<number, IQuestionnaireDetailed>();

    for (const q of questionnaires) {
      const existing = latestByTemplate.get(q.questionnaireTemplateId);

      if (!existing || q.id > existing.id) {
        latestByTemplate.set(q.questionnaireTemplateId, q);
      }
    }

    const uniqueQuestionnaires = Array.from(latestByTemplate.values());

    try {
      const sections = await Promise.all(
        uniqueQuestionnaires.map(async (q) => {
          const rows = await this.getQuestionnaireData(
            q.questionnaireTemplateId,
          );

          if (!rows.length) {
            return null;
          }

          return {
            questionnaireId: q.id,
            questionnaireTemplateId: q.questionnaireTemplateId,
            title: rows[0]?.title ?? '',
            rows,
          };
        }),
      );

      this.questionnaireSections = sections.filter(
        (
          s,
        ): s is {
          questionnaireId: number;
          questionnaireTemplateId: number;
          title: string;
          rows: any[];
        } => !!s && s.rows.length > 0,
      );
    } catch (e) {
      this.questionnaireSections = [];
      this.swalToastService.toastError('Unable to retrieve Questionnaire Data');
    } finally {
      this.isLoading = false;
    }
  }

  getQuestionnaireData(templateId: number): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.questionnaireService
        .getQuestionnaireData(templateId) // questionnaire template id (inpatient = 1, outpatient = 2)
        .subscribe(
          (data: IQuestionnaireDetailed) => {
            if (data && data.questionnaire && data.answers) {
              const questionnaire = data.questionnaire;
              const answers = data.answers.answer;
              // Total number of pages
              const totalNumberOfPages = questionnaire.pages.length;

              const totals =
                templateId === 2
                  ? this.computeFastTotals(answers)
                  : templateId === 16
                    ? this.computePacslacTwoTotals(questionnaire, answers)
                    : this.computeQuestionnaireCompletionTotals(
                        questionnaire,
                        answers,
                        templateId,
                      );
              const totalNumberOfQuestions = totals.totalQuestions;
              const questionsCompleted = totals.completed;

              // Number of questions remaining
              const questionsRemaining = Math.max(
                totalNumberOfQuestions - questionsCompleted,
                0,
              );

              // Completion percentage
              const completionPercentage =
                totalNumberOfQuestions > 0
                  ? (
                      (questionsCompleted / totalNumberOfQuestions) *
                      100
                    ).toFixed(2) + '%'
                  : '0%';

              // Determine the status
              const status = data.isSubmitted
                ? 'Submitted'
                : 'Not Submitted (Ready for Submission)';

              // Create the data object
              const questionaireDataDto = [
                {
                  dateTime: data.creationDate || '',
                  title: questionnaire.title,
                  totalNumberOfPages: `${totalNumberOfPages} pages`,
                  totalNumberOfQuestions: `${totalNumberOfQuestions} questions`,
                  completionPercentage: `${Math.round(
                    parseFloat(completionPercentage),
                  )}% completed`,
                  questionsCompleted: `${questionsCompleted} out of ${totalNumberOfQuestions} questions completed`,
                  questionsRemaining: `${questionsRemaining} questions remaining`,
                  status,
                },
              ];

              // Resolve the promise with the data
              resolve(questionaireDataDto);
            } else {
              resolve([]); // Resolve with an empty array if there's no data
            }
          },
          (error) => {
            reject(error); // Reject the promise in case of an error
          },
        );
    });
  }

  private computeFastTotals(answers: any[]): {
    totalQuestions: number;
    completed: number;
  } {
    const answeredIds = new Set<number>(
      answers.map((a) => Number(a.questionTemplateId)),
    );

    // Fast Protocol always has exactly 4 pages (Face, Arm, Speech, Time).
    // The side question (q47/q48) is part of its page, not a separate question.
    const totalQuestions = 4;

    // A page is complete when any main answer for it exists.
    // q47/q48 (left/right side) are sub-details of their page, not counted separately.
    let completed = 0;
    if (answeredIds.has(40) || answeredIds.has(41)) completed += 1; // Face
    if (answeredIds.has(42) || answeredIds.has(43)) completed += 1; // Arm
    if (answeredIds.has(44) || answeredIds.has(46)) completed += 1; // Speech
    if (answeredIds.has(45)) completed += 1; // Time

    return { totalQuestions, completed };
  }

  //specific completion count, mirroring the backend's
  // CalculatePacslacTwoScore/GetPacslacTwoScoredCoverage: a "section"
  // element starts a new group, and every checkbox/boolean element after
  //it belongs to that group until the next section.
  private computePacslacTwoTotals(
    questionnaire: IQuestionnaire,
    answers: GetAnswerDto[],
  ): { totalQuestions: number; completed: number } {
    const elements = questionnaire.pages.flatMap((p) => p.elements ?? []);
    const totalScoreControlName = elements.find(
      (element: IQuestionnaireQuestion) =>
        element.type === 'text' && /total/i.test(element.title ?? ''),
    )?.name;

    const answeredIds = new Set(
      (answers ?? [])
        .map((answer: GetAnswerDto) => Number(answer.questionTemplateId))
        .filter((id: number) => Number.isFinite(id)),
    );

    const groups: IQuestionnaireQuestion[][] = [];
    let currentGroup: IQuestionnaireQuestion[] | null = null;

    for (const element of elements) {
      const questionType = (element.type ?? '').toLowerCase();

      if (questionType === EQuestionType.SECTION) {
        currentGroup = [];
        groups.push(currentGroup);
        continue;
      }

      if (
        (questionType !== 'checkbox' &&
          questionType !== 'boolean' &&
          questionType !== 'text') ||
        element.name === totalScoreControlName
      ) {
        continue;
      }

      if (currentGroup) {
        currentGroup.push(element);
      } else {
        groups.push([element]);
      }
    }

    const questionGroups = groups.filter((group) => group.length > 0);
    const totalQuestions = questionGroups.length;
    const completed = questionGroups.filter((group) =>
      group.some((element: IQuestionnaireQuestion) =>
        answeredIds.has(Number(element.id)),
      ),
    ).length;

    return {
      totalQuestions,
      completed: Math.min(completed, totalQuestions),
    };
  }

  private computeQuestionnaireCompletionTotals(
    questionnaire: IQuestionnaire,
    answers: GetAnswerDto[],
    templateId: number,
  ): { totalQuestions: number; completed: number } {
    const elements = questionnaire.pages.flatMap((p) => p.elements ?? []);
    const excludedScoreNames = this.getExcludedScoreControlNames(
      elements,
      templateId,
    );
    const answerableQuestionIds = new Set(
      elements
        .filter((element: IQuestionnaireQuestion) =>
          this.isAnswerableQuestion(element, excludedScoreNames),
        )
        .map((element: IQuestionnaireQuestion) => Number(element.id))
        .filter((id: number) => Number.isFinite(id)),
    );
    const totalQuestions = answerableQuestionIds.size;
    const completed = (answers ?? []).filter((answer: GetAnswerDto) =>
      answerableQuestionIds.has(Number(answer.questionTemplateId)),
    ).length;

    return {
      totalQuestions,
      completed: Math.min(completed, totalQuestions),
    };
  }

  private isAnswerableQuestion(
    element: IQuestionnaireQuestion,
    excludedScoreNames: Set<string>,
  ): boolean {
    const questionType = (element.type ?? '').toLowerCase();

    return (
      questionType !== EQuestionType.SECTION &&
      questionType !== EQuestionType.HEADER &&
      !excludedScoreNames.has(element.name)
    );
  }

  private getExcludedScoreControlNames(
    elements: IQuestionnaireQuestion[],
    templateId: number,
  ): Set<string> {
    const knownScoreControlNames: Record<number, string[]> = {
      3: ['question62'],
      5: ['question86'],
      6: ['question108'],
      8: ['question458'],
    };
    const excludedNames = new Set<string>(
      knownScoreControlNames[templateId] ?? [],
    );

    if (templateId === 10) {
      const sldcTotalControlName = elements.find(
        (element: IQuestionnaireQuestion) =>
          element.type !== 'radiogroup' && !!element.name,
      )?.name;

      if (sldcTotalControlName) {
        excludedNames.add(sldcTotalControlName);
      }
    }

    return excludedNames;
  }
}
